import { Fragment } from 'react';

import { expertNoviceSessionSummary } from './expertNoviceSession';
import type { ExpertNoviceParticipant } from './expertNoviceSession';
import type { ReactNode } from 'react';

export function ExpertNoviceSessionDetails<T extends ExpertNoviceParticipant>({
  participants,
  renderParticipant,
}: {
  participants: readonly (T | null)[];
  renderParticipant: (participant: T) => ReactNode;
}) {
  const summary = expertNoviceSessionSummary(participants);
  return (
    <section className="expertNoviceDetails" aria-label="Assistant and user places" aria-live="polite">
      <div className="pairSummary">
        <strong className={`pairBadge pair-${summary.tone}`}>{summary.label}</strong>
        <span>{summary.confirmed}/2 confirmed</span>
      </div>
      {summary.conflict && (
        <p className="text-destructive">Two participants hold the same role. Move one before this session is full.</p>
      )}
      {!summary.conflict && summary.unassignedConfirmed > 0 && (
        <p className="text-destructive">Give every confirmed participant a role before this session counts as full.</p>
      )}
      {(['expert', 'novice'] as const).map(role => (
        <div key={role} className="pairSeat">
          <div className="pairSeatHeading">
            <strong>{role === 'expert' ? 'Assistant' : 'User'}</strong>
            <span>{role === 'expert' ? summary.expertsConfirmed : summary.novicesConfirmed}/1 confirmed</span>
          </div>
          {summary[role].length === 0 ? (
            <p className="text-muted-foreground">No participant invited</p>
          ) : (
            summary[role].map(participant => (
              <Fragment key={participant._id}>{renderParticipant(participant)}</Fragment>
            ))
          )}
          {summary[role].some(p => p.status === 4) && (
            <p className="pairPending">Invitation sent · awaiting confirmation</p>
          )}
        </div>
      ))}
      {summary.unassigned.length > 0 && (
        <div className="pairSeat">
          <strong>Role not assigned</strong>
          {summary.unassigned.map(participant => (
            <Fragment key={participant._id}>{renderParticipant(participant)}</Fragment>
          ))}
        </div>
      )}
    </section>
  );
}
